import Error from "@mui/icons-material/Error";
import { Stack, Typography } from "@mui/material";
import { DataGrid } from "components/DataGrid";
import { IssueCount } from "components/IssueCount";
import { prettyPrintQuantity } from "encamp-shared/src/utils/prettyPrintUnits";
import { ReportingFacilityChemicalQuery } from "generated-graphql/graphql";
import { useFacility } from "routes/Customer/Facility/useFacility";

import { NoRowsOverlay } from "../Inventory/Facility/NoRowsOverlay";
import { useReport } from "../Report/useReport";

export const StorageLocationsTable = ({
  rfc,
}: {
  rfc: ReportingFacilityChemicalQuery["reportingFacilityChemical"];
}) => {
  const { data: reportData } = useReport();
  const { data: facilityData } = useFacility();

  const state = reportData?.tierIIReport?.facility?.state ?? facilityData?.facility?.state;

  const isOregonFacility = state === "OR";

  return (
    <Stack spacing={1}>
      <Typography variant="h6">Storage Locations</Typography>
      <DataGrid
        pagination
        pageSizeOptions={[5]}
        disableRowSelectionOnClick
        initialState={{
          pagination: { paginationModel: { pageSize: 5 } },
        }}
        slots={{
          noRowsOverlay: () => <NoRowsOverlay message="No storage locations found." />,
        }}
        rows={rfc.storageLocations ?? []}
        columns={[
          {
            field: "location",
            headerName: "Location",
            flex: 1,
            valueGetter(_, row) {
              return isOregonFacility
                ? row.storageLocation?.OR_storageBuilding
                : row.storageLocation?.description;
            },
          },
          {
            field: "storageType",
            headerName: "Storage Type",
            flex: 1,
            valueGetter(_, row) {
              return row.storageType;
            },
          },
          {
            field: "maxAmount",
            headerName: "Max Amount",
            flex: 1,
            valueGetter(_, row) {
              return prettyPrintQuantity({
                amount: row.maxAmount,
                unit: row.unit,
                state,
              });
            },
          },
          {
            field: "issues",
            headerName: "Issues",
            flex: 1,
            renderCell({ row }) {
              const issues = rfc.issues?.filter(
                (issue) =>
                  issue.modelName === "ReportingFacilityChemicalStorageLocation" &&
                  issue.modelId === row.id
              );
              return <IssueCount issueCount={issues.length} />;
            },
          },
        ]}
      />
      {rfc.hasConfidentialStorageLocations && (
        <Typography sx={{ display: "flex", gap: 1 }}>
          <Error color="warning" />
          This chemical has confidential storage locations
        </Typography>
      )}
    </Stack>
  );
};
